/**
 * Strategy Service
 * Handles all strategy-related API calls to the auth-api backend.
 */

import { useAuthStore } from "../stores/authStore";
import { useSessionExpiredStore } from "../stores/sessionExpiredStore";

const API_BASE = import.meta.env.VITE_AUTH_API_URL || "http://localhost:8081";

// Types
export interface StrategyMetrics {
  cagr?: number;
  sharpe?: number;
  sortino?: number;
  max_drawdown?: number;
  volatility?: number;
  calmar?: number;
  total_return?: number;
  trading_days?: number;
  alpha?: number;
  beta?: number;
  r_squared?: number;
  correlation?: number;
  trailing_1m?: number;
  trailing_3m?: number;
  trailing_6m?: number;
  trailing_1y?: number;
  best_year?: number;
  worst_year?: number;
  metrics_updated_at?: string;
}

export interface Strategy {
  id: string;
  user_id?: string;
  name: string;
  slug: string;
  description?: string;
  code: string;

  source_strategy_id?: string;
  is_community: boolean;
  is_bookmarked?: boolean;
  visibility: "private" | "unlisted" | "public";
  is_featured: boolean;
  is_trending: boolean;
  is_enabled: boolean;
  execution_time?: string;
  last_executed_at?: string;
  threshold_pct?: number; // Rebalance threshold (0 = daily, 0.05 = 5% drift)
  metrics: StrategyMetrics;
  created_at: string;
  updated_at: string;
}

export interface StrategyListItem {
  id: string;
  user_id?: string;
  name: string;
  slug: string;
  description?: string;
  is_community: boolean;
  visibility: "private" | "unlisted" | "public";
  is_featured: boolean;
  is_trending: boolean;
  // Metrics for filtering
  cagr?: number;
  sharpe?: number;
  sortino?: number;
  max_drawdown?: number;
  volatility?: number;
  calmar?: number;
  total_return?: number;
  best_year?: number;
  worst_year?: number;
  trailing_1m?: number;
  trailing_3m?: number;
  trailing_6m?: number;
  trailing_1y?: number;
  default_weight?: number;
  // Engagement metrics
  view_count: number;
  copy_count: number;
  like_count: number;
  is_liked: boolean;
  is_bookmarked: boolean;
  created_at: string;
}

export interface StrategiesListResponse {
  strategies: StrategyListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreateStrategyRequest {
  name: string;
  description?: string;
  code: string;
  visibility?: "private" | "unlisted" | "public";
  is_community?: boolean;
  metrics?: {
    cagr?: number;
    sharpe?: number;
    sortino?: number;
    maxDrawdown?: number;
    volatility?: number;
    calmar?: number;
    bestYear?: number;
    worstYear?: number;
    totalReturn?: number;
    tradingDays?: number;
    trailing1m?: number;
    trailing3m?: number;
    trailing6m?: number;
    trailing1y?: number;
    alpha?: number;
    beta?: number;
    rSquared?: number;
    correlation?: number;
  };
}

export interface UpdateStrategyRequest {
  name?: string;
  description?: string;
  code?: string;
  visibility?: "private" | "unlisted" | "public";
}

export interface InvestRequest {
  amount: number;
}

// Helper function for authenticated requests
// silentOn401: if true, don't trigger session expired modal on 401 (for fire-and-forget calls)
async function authRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  silentOn401: boolean = false,
): Promise<T> {
  // Get token directly from Zustand store
  const token = useAuthStore.getState().accessToken;

  console.log(
    "[authRequest] Token from store:",
    token ? token.substring(0, 50) + "..." : "null",
  );

  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  const data = await response.json();

  if (!response.ok) {
    // Handle 401 Unauthorized - session expired (unless silent mode)
    if (response.status === 401 && !silentOn401) {
      console.warn("[strategyService] 401 Unauthorized - triggering session expired modal");
      useSessionExpiredStore.getState().setExpired(
        data.error === "Unauthorized" || data.error === "Request failed"
          ? "Your session has expired. Please log in again to continue."
          : data.error
      );
    }
    
    throw new Error(data.error || "Request failed");
  }

  return data as T;
}

// ============================================
// Community Strategies (Public)
// ============================================

export async function getCommunityStrategies(
  limit = 20,
  offset = 0,
): Promise<StrategiesListResponse> {
  return authRequest<StrategiesListResponse>(
    `/api/v1/community/strategies?limit=${limit}&offset=${offset}`,
  );
}

export async function getCommunityStrategy(id: string): Promise<Strategy> {
  return authRequest<Strategy>(`/api/v1/community/strategies/${id}`);
}

// ============================================
// Benchmark Strategies
// ============================================

export interface BenchmarkItem {
  name: string;
  slug: string;
  cagr?: number;
  sharpe?: number;
  max_drawdown?: number;
  trailing_1m?: number;
  trailing_3m?: number;
  trailing_6m?: number;
  trailing_1y?: number;
}

export async function getBenchmarks(): Promise<BenchmarkItem[]> {
  const data = await authRequest<{ benchmarks: BenchmarkItem[] }>(
    `/api/v1/community/benchmarks`,
  );
  return data.benchmarks;
}

// ============================================
// User Strategies (Authenticated)
// ============================================

export async function getMyStrategies(
  limit = 20,
  offset = 0,
): Promise<StrategiesListResponse> {
  return authRequest<StrategiesListResponse>(
    `/api/v1/strategies?limit=${limit}&offset=${offset}`,
  );
}

export async function getStrategy(id: string): Promise<Strategy> {
  return authRequest<Strategy>(`/api/v1/strategies/${id}`);
}

export async function createStrategy(
  request: CreateStrategyRequest,
): Promise<Strategy> {
  return authRequest<Strategy>("/api/v1/strategies", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

export async function updateStrategy(
  id: string,
  request: UpdateStrategyRequest,
): Promise<Strategy> {
  const result = await authRequest<Strategy>(`/api/v1/strategies/${id}`, {
    method: "PATCH",
    body: JSON.stringify(request),
  });

  // CRITICAL: If code was updated, invalidate AST cache so live trading
  // picks up the new strategy code. The auth-api does this server-side too,
  // but we also call the backtest-api endpoint as belt-and-suspenders.
  if (request.code) {
    invalidateASTCache(id).catch((err) =>
      console.warn("[strategyService] AST cache invalidation (secondary) failed:", err),
    );
  }

  return result;
}

/**
 * Invalidate the AST cache for a strategy.
 * CRITICAL for live trading: ensures the executor uses the latest strategy code.
 */
export async function invalidateASTCache(strategyId: string): Promise<void> {
  const API_PROXY = import.meta.env.VITE_API_URL || "";
  try {
    const resp = await fetch(
      `${API_PROXY}/api/cache/invalidate-ast/${strategyId}`,
      { method: "POST" },
    );
    if (!resp.ok) {
      console.warn(`[AST-CACHE] Invalidation returned ${resp.status}`);
    } else {
      console.log(`[AST-CACHE] Cache invalidated for strategy ${strategyId}`);
    }
  } catch (err) {
    console.error("[AST-CACHE] Invalidation request failed:", err);
  }
}

export async function deleteStrategy(id: string): Promise<{ message: string }> {
  return authRequest<{ message: string }>(`/api/v1/strategies/${id}`, {
    method: "DELETE",
  });
}

// ============================================
// Bookmarks / Watchlist
// ============================================

export async function getBookmarkedStrategies(
  limit = 20,
  offset = 0,
): Promise<StrategiesListResponse> {
  return authRequest<StrategiesListResponse>(
    `/api/v1/strategies/bookmarks?limit=${limit}&offset=${offset}`,
  );
}

export interface BookmarkResponse {
  message: string;
  strategy_copy?: Strategy; // Present when a private copy was created
}

export async function bookmarkStrategy(
  id: string,
  notes?: string,
): Promise<BookmarkResponse> {
  return authRequest<BookmarkResponse>(`/api/v1/strategies/${id}/bookmark`, {
    method: "POST",
    body: JSON.stringify({ notes }),
  });
}

export async function unbookmarkStrategy(
  id: string,
): Promise<{ message: string }> {
  return authRequest<{ message: string }>(`/api/v1/strategies/${id}/bookmark`, {
    method: "DELETE",
  });
}

// ============================================
// Investing
// ============================================

export interface InvestResponse {
  message: string;
  investment: {
    id: string;
    portfolio_id: string;
    strategy_id: string;
    allocation_percent: number;
    invested_amount: number;
    current_value: number;
    is_active: boolean;
    started_at: string;
  };
  strategy_copy: Strategy;
}

export async function investInStrategy(
  id: string,
  amount: number,
  brokerageAccountId: string,
): Promise<InvestResponse> {
  if (!brokerageAccountId) {
    throw new Error("Brokerage account is required for investments");
  }
  return authRequest<InvestResponse>(`/api/v1/strategies/${id}/invest`, {
    method: "POST",
    body: JSON.stringify({ 
      amount,
      brokerage_account_id: brokerageAccountId,
    }),
  });
}

// ============================================
// Sharing
// ============================================

export interface ShareResponse {
  message: string;
  community_strategy: Strategy;
}

export async function shareStrategyToCommunity(
  id: string,
): Promise<ShareResponse> {
  return authRequest<ShareResponse>(`/api/v1/strategies/${id}/share`, {
    method: "POST",
  });
}

// ============================================
// Likes
// ============================================

export async function likeStrategy(id: string): Promise<{ message: string }> {
  return authRequest<{ message: string }>(`/api/v1/strategies/${id}/like`, {
    method: "POST",
  });
}

export async function unlikeStrategy(id: string): Promise<{ message: string }> {
  return authRequest<{ message: string }>(`/api/v1/strategies/${id}/like`, {
    method: "DELETE",
  });
}

// ============================================
// Backtest Logging
// ============================================

export interface LogBacktestRequest {
  strategy_id?: string;
  strategy_name?: string;
  days: number;
  cagr?: number;
  sharpe?: number;
  max_drawdown?: number;
  duration_ms?: number;
}

export async function logBacktestRun(
  request: LogBacktestRequest,
): Promise<{ message: string }> {
  // Use silentOn401=true to not show session expired modal for background logging
  return authRequest<{ message: string }>(`/api/v1/strategies/backtest/log`, {
    method: "POST",
    body: JSON.stringify(request),
  }, true);
}
